let audioCtx: AudioContext | null = null;
const heardIds = new Set<number>();
const HEARD_CAP = 120;

const AudioCtx =
  typeof window === 'undefined'
    ? null
    : window.AudioContext
      || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

const getCtx = (): AudioContext | null => {
  if (!AudioCtx) return null;
  if (!audioCtx) audioCtx = new AudioCtx();
  return audioCtx;
};

export const unlockCentreNotifSound = () => {
  const ctx = getCtx();
  if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
};

const tone = (
  ctx: AudioContext,
  dest: AudioNode,
  {
    freq,
    start,
    dur,
    type = 'triangle',
    peak = 0.2
  }: { freq: number; start: number; dur: number; type?: OscillatorType; peak?: number }
) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain);
  gain.connect(dest);
  osc.start(start);
  osc.stop(start + dur + 0.02);
};

const playAlert = (ctx: AudioContext) => {
  const t0 = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.85;
  master.connect(ctx.destination);
  tone(ctx, master, { freq: 880, start: t0, dur: 0.11, type: 'triangle', peak: 0.22 });
  tone(ctx, master, { freq: 1174, start: t0 + 0.13, dur: 0.16, type: 'triangle', peak: 0.2 });
};

const playSoft = (ctx: AudioContext) => {
  const t0 = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.45;
  master.connect(ctx.destination);
  tone(ctx, master, { freq: 1400, start: t0, dur: 0.055, type: 'sine', peak: 0.07 });
};

let ringTimer = 0;

const ringbackBurst = (ctx: AudioContext) => {
  const t0 = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.35;
  master.connect(ctx.destination);
  tone(ctx, master, { freq: 440, start: t0, dur: 0.4, type: 'sine', peak: 0.12 });
  tone(ctx, master, { freq: 480, start: t0, dur: 0.4, type: 'sine', peak: 0.08 });
  tone(ctx, master, { freq: 440, start: t0 + 0.5, dur: 0.4, type: 'sine', peak: 0.12 });
  tone(ctx, master, { freq: 480, start: t0 + 0.5, dur: 0.4, type: 'sine', peak: 0.08 });
};

export const startRingback = () => {
  stopRingback();
  const ctx = getCtx();
  if (!ctx) return;
  const play = () => {
    ringbackBurst(ctx);
    ringTimer = window.setInterval(() => ringbackBurst(ctx), 4000);
  };
  if (ctx.state === 'suspended') {
    void ctx.resume().then(play).catch(() => {});
    return;
  }
  play();
};

export const stopRingback = () => {
  if (ringTimer) window.clearInterval(ringTimer);
  ringTimer = 0;
};

export const playCentreIncomingSound = (kind: 'alert' | 'soft', messageId: number) => {
  if (!messageId || heardIds.has(messageId)) return;
  heardIds.add(messageId);
  if (heardIds.size > HEARD_CAP) {
    const oldest = heardIds.values().next().value;
    if (oldest != null) heardIds.delete(oldest);
  }

  const ctx = getCtx();
  if (!ctx) return;
  const run = () => {
    if (kind === 'soft') playSoft(ctx);
    else playAlert(ctx);
  };
  if (ctx.state === 'suspended') {
    void ctx.resume().then(run).catch(() => {});
    return;
  }
  run();
};
